import { memo, useEffect, useRef, useState, type ClipboardEvent, type MouseEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { cn } from "../../lib/cn";
import { chatTimestamp, clock } from "../../lib/format";
import { Avatar } from "../../components/ui/Avatar";
import { Tooltip } from "../../components/ui/Tooltip";
import { Menu, MenuContent, MenuTrigger } from "../../components/ui/Menu";
import { RichText } from "../../components/RichText";
import { NameTags } from "../../components/UserBadges";
import { ChatMedia } from "../../components/Media";
import { toast } from "../../components/ui/Toast";
import { IconArrowRightUp, IconCopy, IconEdit, IconIdCard, IconLink, IconLock, IconMore, IconPin, IconReply, IconSlashCommand, IconSmile, IconTrash } from "../../components/icons";
import { copyText } from "../posts/usePostActions";
import { UploadPreviews } from "../posts/Composer";
import { useUploads } from "../posts/useUploads";
import { EmojiPicker } from "./EmojiPicker";
import { ChatGif, inlineGifs } from "./ChatGif";
import { PollCard } from "./PollCard";
import { SystemMessage } from "./SystemMessage";
import { DropdownActionItems, MessageContextMenu, UserContextMenu, copyWithToast, type ActionSections } from "./MessageMenus";
import { recordEmojiUse, useQuickReactions } from "./quickReactions";
import { shiftClickMention } from "./composerBus";
import type { ChatMessage, MessageAction, MessageActions } from "./types";
import { usePrefs } from "../../lib/prefs";
import { inviteCodesIn } from "../../lib/invites";
import { pastedImages } from "../../lib/clipboardFiles";
import { InviteEmbed } from "../communities/InviteCard";

/**
 * Inline editor. With `canAttach`, pasting images (Ctrl/Cmd+V) uploads them
 * straight away and saving adds them to the message's attachments.
 */
function EditBox({
  initial,
  onSave,
  onCancel,
  canAttach,
  existingMedia,
}: {
  initial: string;
  onSave: (v: string, mediaIds: string[]) => Promise<void>;
  onCancel: () => void;
  canAttach?: boolean;
  existingMedia: number;
}) {
  const [v, setV] = useState(initial);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  const uploads = useUploads({ max: Math.max(0, 10 - existingMedia), allowVideo: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 300)}px`;
    }
  }, [v]);
  const save = async () => {
    const t = v.trim();
    if (uploads.uploading) return;
    if (!t || (t === initial && uploads.mediaIds.length === 0)) return onCancel();
    setBusy(true);
    try {
      await onSave(t, uploads.mediaIds);
      onCancel();
    } catch {
      setBusy(false);
    }
  };
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (!canAttach) return;
    const files = pastedImages(e.clipboardData);
    if (!files.length) return;
    e.preventDefault();
    if (existingMedia + uploads.items.length >= 10) return void toast.error("A message can have up to 10 attachments");
    uploads.add(files);
  };
  return (
    <div className="mt-1">
      <textarea
        ref={ref}
        value={v}
        disabled={busy}
        onChange={(e) => setV(e.target.value)}
        onPaste={onPaste}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void save();
          }
        }}
        aria-label="Edit message"
        className="w-full resize-none rounded-md border border-line-strong bg-surface px-3 py-2 text-base leading-[1.4rem] text-fg focus:border-accent focus:outline-none"
      />
      {uploads.items.length > 0 && <UploadPreviews items={uploads.items} onRemove={uploads.remove} />}
      <p className="mt-1 text-xs text-fg-3">
        Escape to <button onClick={onCancel} className="text-accent hover:underline">cancel</button> · Enter to <button onClick={() => void save()} className="text-accent hover:underline">save</button>
        {canAttach && " · Paste to add images"}
      </p>
    </div>
  );
}

/** "Eli used /roll" above a bot's reply to a slash command. */
function InteractionLine({ interaction, className }: { interaction: NonNullable<ChatMessage["interaction"]>; className?: string }) {
  return (
    <div className={cn("mb-0.5 flex min-w-0 items-center gap-1.5 text-xs text-fg-3", className)}>
      <IconSlashCommand className="icon size-3.5 shrink-0" aria-hidden />
      <Avatar src={interaction.user.avatarUrl} name={interaction.user.displayName} size="xs" className="!size-4" />
      <Link to={`/u/${interaction.user.username}`} {...shiftClickMention(interaction.user.username)} className="truncate font-medium text-fg-2 hover:underline">
        {interaction.user.displayName}
      </Link>
      <span className="shrink-0">used</span>
      <span className="truncate font-mono text-accent">/{interaction.name}</span>
    </div>
  );
}

const toolbarBtn = "rounded p-1.5 text-fg-2 hover:bg-surface-3 hover:text-fg";

/** What was under the pointer when the context menu opened. */
interface MenuTarget {
  selection: string;
  href: string | null;
}

export const MessageItem = memo(function MessageItem({
  msg,
  grouped,
  meId,
  meUsername,
  actions,
  highlighted,
  preview,
}: {
  msg: ChatMessage;
  grouped: boolean;
  meId: string;
  meUsername: string;
  actions: MessageActions;
  highlighted?: boolean;
  /** Composer preview: render only, no toolbar or menus. */
  preview?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [target, setTarget] = useState<MenuTarget>({ selection: "", href: null });
  const prefs = usePrefs();
  const quick = useQuickReactions(5);
  const compactMode = prefs.messageDisplay === "compact";
  const colour = prefs.roleColours === "names" ? (actions.authorColour?.(msg.author.id) ?? null) : null;
  const own = msg.author.id === meId;
  // GIF-only messages have no text to edit.
  const canEdit = own && !!msg.content;
  const mentioned = !own && ((msg.mentionIds?.includes(meId) ?? false) || !!msg.mentionsEveryone);
  const interactive = !preview && !msg.pending && !editing;

  if (msg.kind === "system") return <SystemMessage msg={msg} meId={meId} actions={actions} preview={preview} />;

  const myReactions = new Set((msg.reactions ?? []).filter((r) => r.me).map((r) => r.emoji));
  const canReact = actions.canReact && !!actions.onReact && !msg.failed;
  const react = (emoji: string) => {
    const add = !myReactions.has(emoji);
    if (add) recordEmojiUse(emoji);
    actions.onReact?.(msg, emoji, add);
  };

  /* Actions shared by the "⋯" dropdown and the context menu. */
  const sections: ActionSections = [];
  if (!msg.failed) {
    const main: MessageAction[] = [];
    if (actions.canReply) main.push({ key: "reply", label: "Reply", icon: IconReply, onSelect: () => actions.onReply(msg) });
    if (canEdit) main.push({ key: "edit", label: "Edit", icon: IconEdit, onSelect: () => setEditing(true) });
    if (actions.canPin && actions.onPin) main.push({ key: "pin", label: msg.pinnedAt ? "Unpin" : "Pin", icon: IconPin, onSelect: () => actions.onPin!(msg) });
    sections.push(main);
  }
  const copy: MessageAction[] = [];
  if (target.selection) copy.push({ key: "copy-sel", label: "Copy", icon: IconCopy, onSelect: () => void copyWithToast(target.selection) });
  if (target.href) {
    const href = target.href;
    copy.push({ key: "open-link", label: "Open link", icon: IconArrowRightUp, onSelect: () => window.open(href, "_blank", "noopener,noreferrer") });
    copy.push({ key: "copy-link", label: "Copy link", icon: IconLink, onSelect: () => void copyWithToast(href) });
  }
  if (msg.content) copy.push({ key: "copy-text", label: "Copy text", icon: IconCopy, onSelect: () => void copyWithToast(msg.content) });
  if (actions.messageLink && !msg.failed) copy.push({ key: "copy-msg-link", label: "Copy message link", icon: IconLink, onSelect: () => void copyWithToast(actions.messageLink!(msg)) });
  sections.push(copy);
  if (!msg.failed) sections.push(actions.extraActions?.(msg) ?? []);
  if (!msg.failed) sections.push([{ key: "copy-id", label: "Copy message ID", icon: IconIdCard, onSelect: () => void copyWithToast(msg.id) }]);
  if (own || actions.canModerate || msg.failed) sections.push([{ key: "delete", label: "Delete", icon: IconTrash, danger: true, onSelect: () => actions.onDelete(msg) }]);
  // The "⋯" menu has no pointer target: drop the selection/link items there.
  const dropdownSections = sections.map((s) => s.filter((a) => !["copy-sel", "open-link", "copy-link"].includes(a.key)));

  const toolbar = interactive && (
    <div
      className={cn(
        "absolute -top-4 right-3 z-10 flex items-center rounded-md bg-surface-2 p-0.5 shadow-pop",
        pickerOpen ? "flex" : "hidden group-hover:flex group-focus-within:flex",
      )}
    >
      {canReact && (
        <>
          {quick.slice(0, 3).map((e) => (
            <Tooltip key={e} label={myReactions.has(e) ? "Remove reaction" : "React"}>
              <button aria-label={`${myReactions.has(e) ? "Remove reaction" : "React with"} ${e}`} aria-pressed={myReactions.has(e)} onClick={() => react(e)} className={cn(toolbarBtn, "flex size-[30px] items-center justify-center p-0 text-lg leading-none", myReactions.has(e) && "bg-accent-soft")}>
                {e}
              </button>
            </Tooltip>
          ))}
          <span className="mx-0.5 h-5 w-px bg-line" aria-hidden />
          <EmojiPicker open={pickerOpen} onOpenChange={setPickerOpen} onPick={react}>
            <button aria-label="Add reaction" className={toolbarBtn}>
              <IconSmile className="icon size-[18px]" />
            </button>
          </EmojiPicker>
        </>
      )}
      {actions.canReply && (
        <Tooltip label="Reply">
          <button aria-label="Reply" onClick={() => actions.onReply(msg)} className={toolbarBtn}>
            <IconReply className="icon size-[18px]" />
          </button>
        </Tooltip>
      )}
      {canEdit && (
        <Tooltip label="Edit">
          <button aria-label="Edit" onClick={() => setEditing(true)} className={toolbarBtn}>
            <IconEdit className="icon size-[18px]" />
          </button>
        </Tooltip>
      )}
      <Menu>
        <MenuTrigger asChild>
          <button aria-label="More" className={toolbarBtn}>
            <IconMore className="icon size-[18px]" />
          </button>
        </MenuTrigger>
        <MenuContent>
          <DropdownActionItems sections={dropdownSections} />
        </MenuContent>
      </Menu>
    </div>
  );

  const e2eeNotice =
    msg.e2ee === "failed" || msg.e2ee === "locked" ? (
      <span className="inline-flex items-center gap-1 text-sm italic text-fg-3">
        <IconLock className="icon size-3.5" aria-hidden />
        {msg.e2ee === "locked" ? "Encrypted message. Unlock encrypted messages on this device to read it." : "This encrypted message couldn't be decrypted or verified."}
      </span>
    ) : null;
  const failedRow = (
    <>
      {msg.failed && (
        <div className="mt-0.5 text-xs text-danger">
          Couldn't send.{" "}
          {actions.onRetry && (
            <button onClick={() => actions.onRetry!(msg)} className="font-medium underline">
              Retry
            </button>
          )}
        </div>
      )}
    </>
  );
  const reactionsRow = (
    <>
      {!!msg.reactions?.length && (
        <div className="mt-1 flex flex-wrap gap-1">
          {msg.reactions.map((r) => (
            <button
              key={r.emoji}
              disabled={preview || !actions.onReact || (!actions.canReact && !r.me)}
              onClick={() => react(r.emoji)}
              aria-pressed={r.me}
              aria-label={`${r.emoji} ${r.count}`}
              className={cn(
                "inline-flex h-7 items-center gap-1 rounded-md border px-1.5 text-sm transition-colors",
                r.me ? "border-accent/60 bg-accent-soft text-fg" : "border-line bg-surface text-fg-2 hover:border-line-strong",
              )}
            >
              <span className="text-base leading-none">{r.emoji}</span>
              <span className="text-xs font-medium tabular">{r.count}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
  const gifs = inlineGifs(msg.embeds);
  const invites = preview || msg.suppressEmbeds || msg.pending ? [] : inviteCodesIn(msg.content);
  const extrasRow = (
    <>
      {gifs.map((e, i) => (
        <ChatGif key={i} embed={e} />
      ))}
      {invites.map((code) => (
        <InviteEmbed key={code} code={code} />
      ))}
      {msg.poll && (
        <PollCard
          messageId={preview ? undefined : msg.id}
          poll={msg.poll}
          canVote={!!actions.poll && interactive}
          canEnd={!!actions.poll && interactive && (own || actions.canModerate)}
          onVote={(ids) => actions.poll!.vote(msg, ids)}
          onEnd={() => actions.poll!.end(msg)}
        />
      )}
    </>
  );

  const onDoubleClick = () => {
    if (preview || !prefs.doubleClickToReply || !actions.canReply || msg.pending || editing || window.getSelection()?.toString()) return;
    actions.onReply(msg);
  };
  const onContextMenu = (e: MouseEvent) => {
    const sel = window.getSelection();
    const text = sel && !sel.isCollapsed && e.currentTarget.contains(sel.anchorNode) ? sel.toString().trim() : "";
    const a = (e.target as Element).closest?.("a[href^='http']") as HTMLAnchorElement | null;
    setTarget({ selection: text, href: a?.href ?? null });
  };

  // Shift+click on the author's name or avatar mentions them.
  const mentionClick = preview || msg.webhook ? undefined : shiftClickMention(msg.author.username);

  /** The author's name/avatar, with its own context menu. */
  const userMenu = (child: ReactNode) =>
    preview || msg.webhook ? (
      child
    ) : (
      <UserContextMenu user={msg.author} meId={meId} canMention={actions.canReply} moderation={actions.moderation}>
        {child}
      </UserContextMenu>
    );

  const wrap = (row: ReactNode) =>
    preview ? (
      row
    ) : (
      <MessageContextMenu
        disabled={!interactive && !msg.failed}
        sections={sections}
        react={canReact && !msg.pending ? { quick, mine: myReactions, onReact: react } : undefined}
        onOpenChange={setMenuOpen}
      >
        {row}
      </MessageContextMenu>
    );

  if (compactMode) {
    return wrap(
      <div
        id={preview ? undefined : `m-${msg.id}`}
        onDoubleClick={onDoubleClick}
        onContextMenu={onContextMenu}
        className={cn(
          "group relative py-0.5 pl-4 pr-[8%] transition-colors md:pr-4 hover:bg-surface/50 [-webkit-touch-callout:none]",
          !grouped && "mt-1",
          mentioned && "bg-mention before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-accent hover:bg-mention",
          (highlighted || menuOpen) && "bg-accent-soft",
          msg.pending && "opacity-60",
        )}
      >
        {toolbar}
        {msg.interaction && <InteractionLine interaction={msg.interaction} className="pl-14" />}
        {msg.replyTo && (
          <div className="flex items-center gap-1.5 pl-14 text-xs text-fg-3">
            <IconReply className="icon size-3.5 -scale-x-100" aria-hidden />
            <span className="font-medium text-fg-2">{msg.replyTo.author.displayName}</span>
            <span className="truncate">{msg.replyTo.content || (msg.replyTo.hasMedia ? "Image" : "Message")}</span>
          </div>
        )}
        <div className="flex gap-2 text-base leading-[1.4rem]">
          <Tooltip label={new Date(msg.createdAt).toLocaleString("en-GB")}>
            <time dateTime={msg.createdAt} className="w-12 shrink-0 pt-px text-right text-xs leading-[1.4rem] text-fg-3 tabular">
              {clock(msg.createdAt)}
            </time>
          </Tooltip>
          <div className="min-w-0 flex-1">
            {userMenu(
              <Link to={`/u/${msg.author.username}`} {...mentionClick} className="mr-1.5 font-semibold text-fg hover:underline" style={{ color: colour ?? undefined }}>
                {msg.author.displayName}
              </Link>,
            )}
            <NameTags user={msg.author} className="mr-1.5 align-middle" />
            {editing ? (
              <EditBox initial={msg.content} onSave={(v, mediaIds) => actions.onEdit(msg, v, mediaIds)} onCancel={() => setEditing(false)} canAttach={actions.canAttach} existingMedia={msg.media.length} />
            ) : (
              <>
                {msg.content ? <RichText text={msg.content} me={meUsername} variant="chat" className="inline text-fg" /> : e2eeNotice}
                {msg.editedAt && <span className="ml-1 text-xs text-fg-3">(edited)</span>}
                {msg.pinnedAt && <IconPin className="icon ml-1 inline size-3.5 text-fg-3" aria-label="Pinned" />}
              </>
            )}
            <ChatMedia media={msg.media} />
            {extrasRow}
            {reactionsRow}
            {failedRow}
          </div>
        </div>
      </div>,
    );
  }

  return wrap(
    <div
      id={preview ? undefined : `m-${msg.id}`}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      className={cn(
        "group relative flex gap-3 pl-4 pr-[8%] transition-colors md:pr-4 hover:bg-surface/50 [-webkit-touch-callout:none]",
        grouped ? "py-0.5" : "mt-3 pt-1 pb-0.5",
        mentioned && "bg-mention before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-accent hover:bg-mention",
        (highlighted || menuOpen) && "bg-accent-soft",
        msg.pending && "opacity-60",
      )}
    >
      {toolbar}
      <div className="w-9 shrink-0">
        {grouped ? (
          <time dateTime={msg.createdAt} className="invisible flex justify-end whitespace-nowrap pt-0.5 text-xs leading-5 text-fg-3 tabular group-hover:visible">
            {clock(msg.createdAt)}
          </time>
        ) : (
          userMenu(
            <Link to={`/u/${msg.author.username}`} {...mentionClick} className={cn("block", msg.interaction && !msg.replyTo ? "mt-[22px]" : "mt-0.5")} aria-label={msg.author.displayName}>
              <Avatar src={msg.author.avatarUrl} name={msg.author.displayName} size="md" />
            </Link>,
          )
        )}
      </div>
      <div className="min-w-0 flex-1">
        {msg.interaction && <InteractionLine interaction={msg.interaction} />}
        {msg.replyTo && (
          <div className="mb-0.5 flex items-center gap-1.5 text-xs text-fg-3">
            <IconReply className="icon size-3.5 -scale-x-100" aria-hidden />
            <Avatar src={msg.replyTo.author.avatarUrl} name={msg.replyTo.author.displayName} size="xs" className="!size-4" />
            <span className="font-medium text-fg-2">{msg.replyTo.author.displayName}</span>
            <button
              className="truncate hover:text-fg"
              onClick={() => document.getElementById(`m-${msg.replyTo!.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" })}
            >
              {msg.replyTo.content || (msg.replyTo.hasMedia ? "Image" : "Message")}
            </button>
          </div>
        )}
        {!grouped && (
          <div className="flex items-baseline gap-2">
            {userMenu(
              <Link to={`/u/${msg.author.username}`} {...mentionClick} className="truncate text-base font-semibold text-fg hover:underline" style={{ color: colour ?? undefined }}>
                {msg.author.displayName}
              </Link>,
            )}
            <NameTags user={msg.author} className="self-center" />
            <Tooltip label={new Date(msg.createdAt).toLocaleString("en-GB")}>
              <time dateTime={msg.createdAt} className="shrink-0 text-xs text-fg-3">
                {chatTimestamp(msg.createdAt)}
              </time>
            </Tooltip>
            {msg.pinnedAt && <IconPin className="icon size-3.5 self-center text-fg-3" aria-label="Pinned" />}
          </div>
        )}
        {editing ? (
          <EditBox initial={msg.content} onSave={(v, mediaIds) => actions.onEdit(msg, v, mediaIds)} onCancel={() => setEditing(false)} canAttach={actions.canAttach} existingMedia={msg.media.length} />
        ) : (
          (msg.content || e2eeNotice) && (
            <div className="text-base leading-[1.4rem] text-fg">
              {e2eeNotice}
              <RichText text={msg.content} me={meUsername} variant="chat" className="inline" />
              {msg.editedAt && (
                <Tooltip label={`Edited ${new Date(msg.editedAt).toLocaleString("en-GB")}`}>
                  <span className="ml-1 text-xs text-fg-3">(edited)</span>
                </Tooltip>
              )}
            </div>
          )
        )}
        <ChatMedia media={msg.media} />
        {extrasRow}
        {failedRow}
        {reactionsRow}
      </div>
    </div>,
  );
});
